import type { NormalizedCacheObject } from "@apollo/client/index.js"
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink
} from "@apollo/client/index.js"
import { onError } from "@apollo/client/link/error"
import type { GraphQLError } from "graphql"
import type { FC, ReactNode } from "react"
import { createContext, useContext, useMemo } from "react"
import type { PublicConfigValues } from "~/providers/ConfigProvider"
import { useConfigContext } from "~/providers/ConfigProvider"

const ContentfulClientContext = createContext<
  undefined | ApolloClient<NormalizedCacheObject>
>(undefined)

export const createContentfulClient = (config: PublicConfigValues) => {
  const httpLink = new HttpLink({
    uri: `https://graphql.contentful.com/content/v1/spaces/${config.contentfulSpace}/environments/${config.contentfulEnvironment}`,
    headers: {
      Authorization: `Bearer ${config.contentfulAccessToken}`
    }
  })

  const links = [
    ApolloLink.from([
      onError(({ response }) => {
        if (response?.errors) {
          // Ignore UNRESOLVABLE_LINK errors which occur when we have draft references in non-preview mode.
          response.errors = response.errors.filter(
            error =>
              isContentfulError(error as GraphQLError) &&
              (error.extensions as any).contentful?.code != "UNRESOLVABLE_LINK"
          )
        }
      }),
      httpLink
    ])
  ]

  const link = ApolloLink.from(links)

  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "cache-and-network"
      }
    }
  })
}

export const ContentfulClientProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const config = useConfigContext()

  const client = useMemo(() => createContentfulClient(config), [config])

  return (
    <ContentfulClientContext.Provider value={client}>
      {children}
    </ContentfulClientContext.Provider>
  )
}

export const useContentfulClient = () => {
  const value = useContext(ContentfulClientContext)

  if (!value) {
    throw new Error("ContentfulClientContext is missing.")
  }

  return value
}

const isContentfulError = (
  error: GraphQLError
): error is GraphQLError & {
  extensions: {
    contentful: {
      code: string
    }
  }
} => !!error.extensions.contentful
